/* import __COLOCATED_TEMPLATE__ from './translation-quality-feedback.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { post, ajaxDelete } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';
import type Snackbar from 'embercom/services/snackbar';
import type IntlService from 'embercom/services/intl';
import { getLanguageLabelFromCode } from '../helpers/translations';

interface TranslationFeedback {
  rating: 1 | -1 | null;
  reasonCode: string | null;
}

interface Args {
  language: string;
  direction: 'inbound' | 'outbound';
  conversationId: number;
  conversationPartId: number;
  sourceLocale: string | undefined;
  targetLocale: string | undefined;
  isVisible: boolean;
  /** Whether the message blocks actually contain translation data. When false, the feedback widget is hidden. */
  hasTranslations?: boolean;
  isShowingOriginal?: boolean;
  /** Which side of the conversation this message belongs to — controls popover alignment. */
  messageSide?: 'teammate' | 'user';
  translationQualityFeedbacks?: Record<string, { rating: 1 | -1; reasonCode: string | null }>;
  /** Whether the part was translated by a workflow. Hides the feedback widget when true. */
  multilingualWorkflowTranslated?: boolean;
}

interface Signature {
  Args: Args;
  Blocks: {
    default: [];
  };
}

const CARD_EDGE_PADDING = 8;
const DEFAULT_CARD_WIDTH = 240;
const DEFAULT_CARD_HEIGHT = 56;

export function shouldShowCardAbove({
  spaceAbove,
  spaceBelow,
  cardHeight,
}: {
  spaceAbove: number;
  spaceBelow: number;
  cardHeight: number;
}): boolean {
  if (spaceBelow >= cardHeight) {
    return false;
  }

  if (spaceAbove >= cardHeight) {
    return true;
  }

  return spaceAbove > spaceBelow;
}

export default class TranslationQualityFeedbackComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare snackbar: Snackbar;
  @service declare intl: IntlService;

  @tracked localFeedbacks: Record<string, TranslationFeedback> = {};
  @tracked isHovered = false;
  @tracked isFeedbackMenuOpen = false;
  @tracked showAbove = false;
  @tracked cardLeft = 0;
  private lastMeasuredCardHeight = DEFAULT_CARD_HEIGHT;

  get isWorkflowTranslated(): boolean {
    return !!this.args.multilingualWorkflowTranslated;
  }

  get feedbacks(): Record<string, TranslationFeedback> {
    return { ...(this.args.translationQualityFeedbacks ?? {}), ...this.localFeedbacks };
  }

  get rating(): 1 | -1 | null {
    return this.feedbacks[this.args.targetLocale ?? '']?.rating ?? null;
  }

  get selectedFeedbackReason(): string | null {
    return this.feedbacks[this.args.targetLocale ?? '']?.reasonCode ?? null;
  }

  get isThumbsUpActive() {
    return this.rating === 1;
  }

  get isThumbsDownActive() {
    return this.rating === -1;
  }

  get showWidget() {
    return this.isHovered || this.isFeedbackMenuOpen;
  }

  get displayLanguage(): string {
    if (this.args.isShowingOriginal) {
      return getLanguageLabelFromCode(this.args.sourceLocale ?? '', this.intl.primaryLocale) || '';
    }
    return this.args.language;
  }

  get showRatingButtons(): boolean {
    return !this.args.isShowingOriginal;
  }

  get cardClass() {
    let width: string;
    if (this.isFeedbackMenuOpen) {
      width = 'w-max min-w-60';
    } else if (this.args.isShowingOriginal) {
      width = 'w-max';
    } else {
      width = 'w-60';
    }
    let vertical = this.showAbove ? 'bottom-full' : 'top-full';
    return `absolute ${vertical} z-50 ${width} rounded-large bg-base-module ring-1 ring-neutral-border shadow-lvl-2`;
  }

  get reasonItems() {
    return [
      {
        id: 'inaccurate_meaning',
        label: this.intl.t(
          'inbox.conversation-stream.translation-quality-feedback.reasons.inaccurate-meaning',
        ),
      },
      {
        id: 'unnatural_phrasing',
        label: this.intl.t(
          'inbox.conversation-stream.translation-quality-feedback.reasons.unnatural-phrasing',
        ),
      },
      {
        id: 'wrong_tone_of_voice',
        label: this.intl.t(
          'inbox.conversation-stream.translation-quality-feedback.reasons.wrong-tone-of-voice',
        ),
      },
      {
        id: 'inappropriate_or_offensive',
        label: this.intl.t(
          'inbox.conversation-stream.translation-quality-feedback.reasons.inappropriate-or-offensive',
        ),
      },
      {
        id: 'incorrect_terminology',
        label: this.intl.t(
          'inbox.conversation-stream.translation-quality-feedback.reasons.incorrect-terminology',
        ),
      },
      {
        id: 'other',
        label: this.intl.t('inbox.conversation-stream.translation-quality-feedback.reasons.other'),
      },
    ];
  }

  @action handleMouseEnter(event: MouseEvent) {
    this.isHovered = true;
    this.updatePosition(event.currentTarget as HTMLElement);
  }

  @action updateCardPosition(element: HTMLElement) {
    let wrapper = element.parentElement;
    if (wrapper) {
      let cardWidth = element.getBoundingClientRect().width || DEFAULT_CARD_WIDTH;
      let cardHeight = element.getBoundingClientRect().height || this.lastMeasuredCardHeight;

      this.lastMeasuredCardHeight = cardHeight;
      this.updatePosition(wrapper, cardWidth, cardHeight);
    }
  }

  private updatePosition(
    wrapper: HTMLElement,
    cardWidth = DEFAULT_CARD_WIDTH,
    cardHeight = this.lastMeasuredCardHeight,
  ) {
    let rect = wrapper.getBoundingClientRect();
    let scrollParent = this.findScrollParent(wrapper);
    let containerRect = scrollParent?.getBoundingClientRect();

    let containerTop = containerRect?.top ?? 0;
    let containerBottom = containerRect?.bottom ?? window.innerHeight;
    let spaceAbove = rect.top - containerTop;
    let spaceBelow = containerBottom - rect.bottom;

    this.showAbove = shouldShowCardAbove({ spaceAbove, spaceBelow, cardHeight });

    if (this.args.messageSide === 'teammate') {
      // End-align: card's right edge meets trigger's right edge
      let endAlignedLeft = rect.width - cardWidth;
      let containerLeft = containerRect?.left ?? 0;
      let minLeft = containerLeft + CARD_EDGE_PADDING - rect.left;
      this.cardLeft = Math.max(endAlignedLeft, minLeft);
    } else {
      // Start-align: card's left edge meets trigger's left edge
      let containerRight = containerRect?.right ?? window.innerWidth;
      this.cardLeft = Math.min(0, containerRight - CARD_EDGE_PADDING - cardWidth - rect.left);
    }
  }

  @action handleMouseLeave() {
    this.isHovered = false;
  }

  @action handleThumbsUp() {
    this.isFeedbackMenuOpen = false;
    if (this.isThumbsUpActive) {
      void this.deleteRating();
    } else {
      void this.saveRating(1, null);
    }
  }

  @action handleThumbsDown() {
    if (this.rating === -1) {
      this.isFeedbackMenuOpen = !this.isFeedbackMenuOpen;
    } else {
      this.isFeedbackMenuOpen = true;
    }
  }

  @action selectFeedbackReason(reasonCode: string) {
    this.isFeedbackMenuOpen = false;
    if (this.rating === -1 && this.selectedFeedbackReason === reasonCode) {
      void this.deleteRating();
    } else {
      void this.saveRating(-1, reasonCode);
    }
  }

  @action closeFeedbackMenu() {
    this.isFeedbackMenuOpen = false;
  }

  private findScrollParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      let { overflowY } = window.getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private async saveRating(rating: 1 | -1, reasonCode: string | null) {
    let locale = this.args.targetLocale ?? '';
    let previousLocalFeedbacks = { ...this.localFeedbacks };

    this.localFeedbacks = { ...this.localFeedbacks, [locale]: { rating, reasonCode } };

    try {
      await post(
        `/ember/conversation_parts/${this.args.conversationPartId}/create_translation_quality_feedback`,
        {
          app_id: this.session.workspace.id,
          conversation_id: this.args.conversationId,
          rating,
          source_locale: this.args.sourceLocale,
          target_locale: this.args.targetLocale,
          reason_code: reasonCode,
        },
      );
    } catch (e: unknown) {
      this.localFeedbacks = previousLocalFeedbacks;
      this.snackbar.notifyError(
        this.intl.t('inbox.conversation-stream.translation-quality-feedback.error'),
      );
    }
  }

  private async deleteRating() {
    let locale = this.args.targetLocale ?? '';
    let previousLocalFeedbacks = { ...this.localFeedbacks };

    this.localFeedbacks = { ...this.localFeedbacks, [locale]: { rating: null, reasonCode: null } };

    try {
      await ajaxDelete(
        `/ember/conversation_parts/${this.args.conversationPartId}/destroy_translation_quality_feedback`,
        {
          app_id: this.session.workspace.id,
          conversation_id: this.args.conversationId,
          target_locale: this.args.targetLocale,
        },
      );
    } catch (e: unknown) {
      this.localFeedbacks = previousLocalFeedbacks;
      this.snackbar.notifyError(
        this.intl.t('inbox.conversation-stream.translation-quality-feedback.error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::Parts::TranslationQualityFeedback': typeof TranslationQualityFeedbackComponent;
    'inbox2/conversation-stream/parts/translation-quality-feedback': typeof TranslationQualityFeedbackComponent;
  }
}
